import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {SnackService} from '../../../../services/snack/snack.service';
import {BookingsService} from '../../../../services/bookings/bookings.service';
import {CustomerService} from '../../../../services/customer/customer.service';
import {LegalPersonService} from '../../../../services/legalPerson/legalPerson.service';
import {AuthService} from '../../../../services/auth/auth.service';
import {TranslateService} from '@ngx-translate/core';
import {CartService} from '../../../../services/cart/cart.service';
import {EventService} from '../../../../services/event/event.service';
import {
  BookingId,
  BookingOptions,
  BookingPost,
  BookingType, CartEvent, EventInfoSmall,
  LegalPersonSimple, TranslationValue
} from '../../../../generated/sales-terminal-client';
import moment from 'moment';
import {isEqual, uniqBy, cloneDeep} from 'lodash-es';
import {LegalPerson, LegalPersonList} from '../../../../generated/sales-terminal-client';
import {ScrollLoad} from '../../../../components/scroll-load-component/ScrollLoad';
import {BookingFormType} from '../bookings.constants';
import {UtilService} from '../../../../services/util/util.service';
import {Customer} from "../../../../generated/customer-client";
import {finalize} from 'rxjs/operators';
import {CONTINUE_WITHOUT_SAVING, UUID_LENGTH} from '../../sales-terminal.constants';
import {Privileges} from '../../../admin/privileges/privileges.constants';
import {Subscription} from 'rxjs';
import {PendingChangesDialogComponent} from '../../../../components/pending-changes-dialog/pending-changes-dialog.component';
import {DatePickerConfig} from '../../../../components/datepicker/datepicker.component';
import {TextInputType} from '../../../../components/forms/text-input/text-input.component';

interface DialogData {
  ticketIds: number[];
}

@Component({
  selector: 'app-bookings-create-modal',
  templateUrl: './bookings-create-modal.component.html',
  styleUrls: ['./bookings-create-modal.component.scss']
})
export class BookingsCreateModalComponent implements OnInit, OnDestroy {
  public customerLegalPerson: LegalPersonSimple = {id: null, name: ''};
  public titles: TranslationValue[][] = [];
  public showCustomerForm: boolean;
  public showLegalPersonForm: boolean;
  public bookingFormType: BookingFormType;
  public BookingFormType = BookingFormType;
  public bookingType = BookingType;
  public bookingOptions: BookingOptions = {};
  public bookingPost: BookingPost = {
    customerUuid: null,
    legalPersonId: null,
    paymentType: null,
    type: BookingType.Client, // default until logic implemented in the UI
    tickets: []
  };
  private bookingPostOrig: BookingPost;
  public isSameOrganiser: boolean;
  public bookingDate = '';
  public dueDate = '';
  public isLoading = false;

  public legalPersonScrollLoad: ScrollLoad<LegalPersonList> = this.legalPersonService.getScrollLoad();
  public customerScrollLoad: ScrollLoad<Customer> = this.customerService.getScrollLoad();
  private selectedLegalPerson: LegalPerson = {} as LegalPerson;
  private selectedCustomer: Customer = {legalPersons: []};
  private _subs: Subscription[] = [];

  public bookingDateConfig: DatePickerConfig = {
    label: 'general.api_reservation_list_period',
    noHint: true, range: true, autoBindDates: true, lockStart: true
  };

  constructor(
    public dialogRef: MatDialogRef<BookingsCreateModalComponent, BookingId>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public utilService: UtilService,
    public authService: AuthService,
    public customerService: CustomerService,
    public legalPersonService: LegalPersonService,
    private snackService: SnackService,
    private bookingsService: BookingsService,
    private translateService: TranslateService,
    public cartService: CartService,
    private eventService: EventService,
    private dialog: MatDialog
  ) {}

  get isModelChanged(): boolean {
    return !isEqual(this.bookingPost, this.bookingPostOrig);
  }

  public ngOnInit(): void {
    this.bookingPost.tickets = this.data.ticketIds || [];
    const uniqueEvents: CartEvent[] = this.cartService.events;
    this.titles = uniqueEvents.map(event => event.title);
    this.bookingsService.getBookingOptions({tickets: this.bookingPost.tickets}).subscribe((options) => {
      this.bookingOptions = options;
      this.bookingDate = moment(this.bookingOptions.bookingDate, 'X').format();
      this.dueDate = moment(this.bookingOptions.dueDate, 'X').format();
      if (this.bookingOptions.deliveryType.length) {
        this.bookingPost.deliveryTypeId = this.bookingOptions.deliveryType[0].id;
      }
      if (this.isPromoter) {
        this.eventService.isSameOrganiser(uniqueEvents.map(event => event.id)).subscribe((value) =>
          this.isSameOrganiser = value.result);
      }
      this.bookingPost.type = this.authService.user.isPromoter ? BookingType.Organiser : BookingType.Client;
      this.bookingFormType = this.authService.user.isPromoter ? BookingFormType.Promoter : BookingFormType.Private;
      this.syncModels();
    });
    this.dialogRef.disableClose = true;
    this._subs.push(this.dialogRef.backdropClick().subscribe(() => {
      this.confirmClose();
    }));
  }

  public ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
    this._subs = [];
  }

  private syncModels(): void {
    this.bookingPostOrig = cloneDeep(this.bookingPost);
  }

  public selectLegalPersonById(legalPersonId: number): void {
    this.resetCustomer();
    this.showCustomerForm = false;
    if (legalPersonId) {
      this.legalPersonService.get(legalPersonId).subscribe((legalPerson) => {
        this.selectLegalPerson(legalPerson);
      });
    }
  }
  public selectLegalPerson(legalPerson: LegalPerson): void {
    this.selectedLegalPerson = legalPerson;
    this.bookingPost.legalPersonId = legalPerson.id;
    this.customerLegalPerson = {id: null, name: ''};
    this.showLegalPersonForm = false;
    this.customerLegalPerson = {id: legalPerson.id, name: legalPerson.companyName};
    this.legalPersonScrollLoad.object = this.selectedLegalPerson;
  }

  public selectCustomerById(customerId: string): void {
    if (customerId) {
      this.customerService.get(customerId).subscribe((customer) => {
        this.selectCustomer(customer);
      });
    }
  }
  public selectCustomer(customer: Customer): void {
    this.selectedCustomer = customer;
    this.bookingPost.customerUuid = this.selectedCustomer.uuid;
    if (this.selectedLegalPerson?.id) {
      const legalPerson = this.selectedCustomer.legalPersons?.find(customerLegalPerson => customerLegalPerson.id === this.selectedLegalPerson.id);
      this.customerLegalPerson = legalPerson || {id: null, name: ''};
    }
    this.showCustomerForm = false;
    this.customerScrollLoad.object = this.selectedCustomer;
  }

  public get isValid(): boolean {
    switch (this.bookingFormType) {
      case BookingFormType.Legal:
        return (this.bookingPost.legalPersonId && this.bookingPost.customerUuid?.length === UUID_LENGTH);
      case BookingFormType.Private:
        return (this.bookingPost.customerUuid?.length === UUID_LENGTH);
      default:
        return true;
    }
  }
  public get isPromoter(): boolean {
    return this.authService.checkPrivileges(Privileges.BOOKING_CREATE_PROMOTER) || this.authService.user.isPromoter;
  }

  public create(): void {
    if (this.isValid) {
      this.isLoading = true;
      this.bookingPost.dueDate = moment(this.dueDate).endOf('day').unix();
      if (this.customerLegalPerson.id) {
        this.bookingPost.legalPersonId = this.customerLegalPerson.id;
      } else {
        delete this.bookingPost.legalPersonId;
      }
      this.bookingsService.addBooking(this.bookingPost).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe((bookingId) => {
        this.dialogRef.close(bookingId);
        this.snackService.showSuccess(this.translateService.instant('general.api_reservation_successfully_created'));
      });
    }
  }

  public clearHiddenFields(): void {
    this.bookingPost.type = BookingType.Client;
    this.bookingPost.deliveryTypeId = this.bookingOptions?.deliveryType[0]?.id;
    if (this.bookingFormType === BookingFormType.Promoter) {
      this.bookingPost.type = BookingType.Organiser;
      this.bookingPost.deliveryTypeId = null;
    }
    this.bookingPost.paymentType = null;
    this.bookingPost.internalComment = '';
    this.showCustomerForm = false;
    this.resetCustomer();
    this.showLegalPersonForm = false;
    this.resetLegalPerson();
  }

  public get displayDeliveryTypeName(): string {
    return this.bookingOptions?.deliveryType?.find(deliveryType => deliveryType.id === this.bookingPost.deliveryTypeId)?.name;
  }

  public displayCustomerForm(): void {
    this.showCustomerForm = true;
    this.showLegalPersonForm = false;
    this.resetCustomer();
  }
  public displayLegalPersonForm(): void {
    this.showLegalPersonForm = true;
    this.showCustomerForm = false;
    this.resetLegalPerson();
  }

  private resetCustomer(): void {
    this.bookingPost.customerUuid = null;
    this.selectedCustomer = {
      legalPersons: []
    };
    this.customerScrollLoad.reset();
  }
  private resetLegalPerson(): void {
    this.bookingPost.legalPersonId = null;
    this.selectedLegalPerson = {} as LegalPerson;
    this.legalPersonScrollLoad.reset();
  }

  public confirmClose(): void {
    if (!this.isModelChanged) {
      this.close();
    } else {
      const pendingChanges = new PendingChangesDialogComponent(this.dialog, this.translateService, {
        pendingChangesDescription: this.translateService.instant('general.api_reservation_description_unsaved_changes'),
        buttonOk: this.translateService.instant('general.api_reservation_make'),
        canNotSave: !this.isValid
      });
      pendingChanges.dialogRef.afterClosed().subscribe((res) => {
        if (res === true) {
          this.create();
        } else if (res === CONTINUE_WITHOUT_SAVING) {
          this.close();
        }
      });
    }
  }

  private close(): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close();
  }

  protected readonly TextInputType = TextInputType;
}
